import React, { useState } from 'react';
import { supabase } from '../global/supabase';
import HamburgerMenu from '../global/menu';
import { LOCATIONS, BRANDING } from '../config';
import '../global/styles.css';

function ChainOfCustodyReport({ userInfo, isAdmin, onLogout, onNavigate }) {
  const [selectedSite, setSelectedSite] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [reportData, setReportData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  // Build location map from config
  const locationTableMap = {};
  LOCATIONS.forEach(loc => {
    locationTableMap[loc.name] = loc.tableName;
  });

  const locations = LOCATIONS.map(loc => loc.name);

  // Redirect if not admin
  if (!isAdmin) {
    return (
      <div className="container-center">
        <div className="card card-center">
          <h1 className="title">Access Denied</h1>
          <p className="subtitle">You do not have administrator privileges.</p>
          <button onClick={() => onNavigate('home')} className="btn btn-primary">
            Return to Dashboard
          </button>
        </div>
      </div>
    );
  }

  const runReport = async () => {
    if (!selectedSite || !startDate || !endDate) {
      alert('Please select a site and both start and end dates.');
      return;
    }

    setLoading(true);
    setReportData([]);

    try {
      const tableName = locationTableMap[selectedSite];

      // Query Supabase for the selected location
      const { data, error } = await supabase
        .from(tableName)
        .select('*')
        .gte('created_at', startDate)
        .lte('created_at', endDate + 'T23:59:59')
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Format data for display
      const formattedData = (data || []).map(record => {
        // Submissions store the signer as first_name/last_name (the user_name
        // column is left empty on insert), so build the display name from those
        // and fall back to user_name for any legacy records.
        const primaryName = `${record.first_name || ''} ${record.last_name || ''}`.trim()
          || record.user_name
          || '';

        return {
          location: selectedSite,
          date: record.created_at ? new Date(record.created_at).toLocaleDateString() : '',
          time: record.created_at ? new Date(record.created_at).toLocaleTimeString() : '',
          userName: primaryName,
          secondaryUserName: record.secondary_user_name || '',
          reason: record.reason_for_change || ''
        };
      });

      setReportData(formattedData);
    } catch (error) {
      console.error('Error loading data:', error);
      alert('Error loading data. Please check console for details.');
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (column) => {
    let direction = 'asc';
    if (sortConfig.key === column && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key: column, direction });

    const sortedData = [...reportData].sort((a, b) => {
      if (a[column] < b[column]) return direction === 'asc' ? -1 : 1;
      if (a[column] > b[column]) return direction === 'asc' ? 1 : -1;
      return 0;
    });

    setReportData(sortedData);
  };

  const exportToCSV = () => {
    if (reportData.length === 0) {
      alert('No data to export.');
      return;
    }

    const headers = ['Location', 'Date', 'Time', 'User', 'Secondary User', 'Reason'];
    const csvData = [
      headers.join(','),
      ...reportData.map(row =>
        [row.location, row.date, row.time, row.userName, row.secondaryUserName, row.reason]
          .map(field => `"${field}"`)
          .join(',')
      )
    ].join('\n');

    const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `chain_of_custody_${BRANDING.companyName.toLowerCase().replace(/\s+/g, '_')}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const getSortIcon = (column) => {
    if (sortConfig.key !== column) return '';
    return sortConfig.direction === 'asc' ? ' ^' : ' v';
  };

  return (
    <div className="container">
      <HamburgerMenu
        isAdmin={isAdmin}
        onLogout={onLogout}
        onNavigate={onNavigate}
      />

      <div className="content content-report">
        {/* Back Button */}
        <button
          onClick={() => onNavigate('admin-dashboard')}
          className="btn btn-secondary"
          style={{
            marginBottom: '20px',
            padding: '10px 20px'
          }}
        >
          Back to Admin
        </button>

        {/* Header */}
        <div className="card" style={{
          textAlign: 'center',
          background: 'var(--primary-color)',
          color: 'white',
          marginBottom: '30px'
        }}>
          <h1 style={{ color: 'white', marginBottom: '10px' }}>
            Chain of Custody Report
          </h1>
          <p style={{ color: 'white', margin: 0 }}>
            Track inventory submissions by location, date, and personnel.
          </p>
        </div>

        {/* Filters */}
        <div className="card" style={{ marginBottom: '30px' }}>
          <h3 className="section-title">Report Filters</h3>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '20px',
            alignItems: 'end'
          }}>
            <div>
              <label className="form-label">Site</label>
              <select
                className="form-input"
                value={selectedSite}
                onChange={(e) => setSelectedSite(e.target.value)}
              >
                <option value="">Select a location...</option>
                {locations.map(location => (
                  <option key={location} value={location}>
                    {location}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="form-label">Start Date</label>
              <input
                type="date"
                className="form-input"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>

            <div>
              <label className="form-label">End Date</label>
              <input
                type="date"
                className="form-input"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>

            <div>
              <button
                className="btn btn-primary btn-full"
                onClick={runReport}
                disabled={loading}
              >
                {loading ? 'Loading...' : 'Run Report'}
              </button>
            </div>

            <div>
              <button
                className="btn btn-secondary btn-full"
                onClick={exportToCSV}
                disabled={reportData.length === 0}
              >
                Export to CSV
              </button>
            </div>
          </div>
        </div>

        {/* Loading Indicator */}
        {loading && (
          <div style={{ textAlign: 'center', margin: '20px 0' }}>
            <p>Loading report...</p>
          </div>
        )}

        {/* Results Table */}
        {reportData.length > 0 && (
          <div className="card">
            <h3 className="section-title">
              Report Results ({reportData.length} records)
            </h3>
            <div style={{ overflowX: 'auto' }}>
              <table style={{
                width: '100%',
                borderCollapse: 'collapse',
                marginTop: '20px'
              }}>
                <thead>
                  <tr style={{ background: 'var(--background-color)' }}>
                    {[
                      { key: 'location', label: 'Location' },
                      { key: 'date', label: 'Date' },
                      { key: 'time', label: 'Time' },
                      { key: 'userName', label: 'User' },
                      { key: 'reason', label: 'Reason' }
                    ].map(column => (
                      <th
                        key={column.key}
                        onClick={() => handleSort(column.key)}
                        style={{
                          padding: '12px',
                          textAlign: 'left',
                          cursor: 'pointer',
                          borderBottom: '2px solid var(--primary-color)',
                          fontWeight: '600',
                          color: 'var(--text-color)'
                        }}
                      >
                        {column.label}{getSortIcon(column.key)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {reportData.map((row, index) => (
                    <tr key={index} style={{
                      borderBottom: '1px solid var(--border-color)',
                      backgroundColor: index % 2 === 0 ? 'white' : 'var(--background-color)'
                    }}>
                      <td style={{ padding: '12px', color: 'var(--text-color)' }}>{row.location}</td>
                      <td style={{ padding: '12px', color: 'var(--text-color)' }}>{row.date}</td>
                      <td style={{ padding: '12px', color: 'var(--text-color)' }}>{row.time}</td>
                      <td style={{ padding: '12px', color: 'var(--text-color)' }}>
                        {row.userName || <span style={{ color: 'var(--text-muted)' }}>—</span>}
                        {row.secondaryUserName && (
                          <div style={{ fontSize: '13px', color: 'var(--text-muted)' }}>
                            + {row.secondaryUserName}
                          </div>
                        )}
                      </td>
                      <td style={{ padding: '12px', color: 'var(--text-color)' }}>{row.reason}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* No Results Message */}
        {!loading && reportData.length === 0 && selectedSite && startDate && endDate && (
          <div className="card" style={{ textAlign: 'center' }}>
            <p style={{ color: 'var(--text-muted)', fontSize: '16px' }}>
              No records found for the selected criteria.
            </p>
          </div>
        )}
      </div>

      <footer className="footer">
        <p className="text-small">
          Chain of Custody Report - {BRANDING.copyright}
        </p>
      </footer>
    </div>
  );
}

export default ChainOfCustodyReport;
